import classNames from "classnames"
import { useMemo } from "react"

import { Analytics } from "@bounce/analytics"
import { DateTime, parseTime } from "@bounce/date"
import type { Coordinates } from "@bounce/google-place"
import { intlNumberFormat, Trans, useLocale, useTranslation } from "@bounce/i18n"
import {
  getDaysOpeningHours,
  getWalkingTime,
  parseOpeningHours,
  parseOpeningHoursExceptions,
  getFirstOpeningHourAfter,
  isClosed,
  is24Hour,
} from "@bounce/store"
import type { NonNullListQueryNode } from "@bounce/util"
import type { PNImageProps } from "@bounce/web-components"
import { PNImage } from "@bounce/web-components"
import { Truncate } from "@bounce/web-components/src/DataDisplay/Truncate/Truncate"

import SafeLink from "../../../../components/Generic/SafeLink/SafeLink"
import { useSectionViewed } from "../../../../hooks/useSectionViewed"
import { InteractionEvents } from "../../../../libs/analytics/events"
import type { Simplify, StoreDocumentData } from "../../../../libs/prismic/types"
import type { Interpolations } from "../hooks/useInterpolations"
import { RichText } from "@/components/Generic/RichText/RichText/RichText"
import type { GetNearbyPoisQuery, GetStoreLocationQuery } from "@/libs/bounce/bounceSdk.types.gen"
import { usePoiUrl } from "@/libs/router"

import CalendarIcon from "@bounce/assets/big-icons/calendar.png"
import StarIcon from "@bounce/assets/big-icons/star.png"
import WalkIcon from "@bounce/assets/big-icons/walk.png"

type GetDistanceInMetersOptions = {
  origin: Coordinates
  destination: Coordinates
}

const convertDegreesToRadians = (degrees: number) => (degrees * Math.PI) / 180
const getDistanceInMeters = ({ origin, destination }: GetDistanceInMetersOptions) => {
  const earthRadiusInMeters = 6371e3

  const dLat = convertDegreesToRadians(destination.latitude - origin.latitude)
  const dLon = convertDegreesToRadians(destination.longitude - origin.longitude)
  const dLat1 = convertDegreesToRadians(origin.latitude)
  const dLat2 = convertDegreesToRadians(destination.latitude)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(dLat1) * Math.cos(dLat2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return earthRadiusInMeters * c
}

type StoreInformationProps = {
  store: GetStoreLocationQuery["store"]
  about?: Simplify<StoreDocumentData>["about"]
  pois: NonNullListQueryNode<GetNearbyPoisQuery["pois"]>[]
  interpolations: Interpolations
  className?: string
}

type StoreInformationSectionProps = {
  children: React.ReactNode
  icon: PNImageProps["src"]
}

const StoreInformationSection = ({ children, icon }: StoreInformationSectionProps) => {
  return (
    <div className="flex flex-row items-center">
      <PNImage
        src={icon}
        height={80}
        width={80}
        loading="lazy"
        imgixParams={{ fit: "max", h: 80 }}
        style={{ width: 80, height: 80 }}
        className="mr-6 object-contain lg:size-[80px]"
      />
      {children}
    </div>
  )
}

type StoreInformationPoiProps = {
  poi: StoreInformationProps["pois"][0]
  city: StoreInformationProps["store"]["city"]
}

const StoreInformationPoi = ({ poi, city }: StoreInformationPoiProps) => {
  const { getUrl } = usePoiUrl({ poi, city })

  return (
    <li key={poi.id} className="my-1">
      <p className="label1">
        <Trans
          i18nKey="screen.storeScreen.storeInformation.walkingTime"
          defaults="{{walkingTime}} from <a>{{name}}</a>"
          values={poi}
          components={{ a: <SafeLink href={getUrl()} /> }}
        />
      </p>
    </li>
  )
}

export const StoreInformation = ({ store, about, pois, interpolations, className }: StoreInformationProps) => {
  const { t, i18n } = useTranslation("marketing-ls-store")
  const { locale } = useLocale()
  const { sectionRef } = useSectionViewed({ name: "StoreInformation" })

  const openingHoursText = useMemo(() => {
    const { openingHours, openingHourExceptions } = store
    const parsedOpeningHours = parseOpeningHours(openingHours)
    const parsedExceptions = parseOpeningHoursExceptions(openingHourExceptions)

    const today = DateTime.local()

    const todaysOpeningHours = getDaysOpeningHours(today, parsedOpeningHours, parsedExceptions)
    if (todaysOpeningHours.length === 0 || isClosed(todaysOpeningHours)) {
      const nextOpeningHour = getFirstOpeningHourAfter(today, parsedOpeningHours, parsedExceptions)
      const nextOpenTime = parseTime(nextOpeningHour?.hour.openTime ?? "", today)
      const day = nextOpeningHour?.date.toLocaleString({ weekday: "long" }, { locale })
      const nextOpen = nextOpenTime?.toLocaleString(DateTime.TIME_24_SIMPLE, { locale })

      return (
        <Trans
          ns="marketing-ls-store"
          i18nKey="screen.storeScreen.storeInformation.nextOpening"
          defaults="Opens on <br>{{openDay}} at {{openTime}}"
          values={{ openTime: nextOpen, openDay: day }}
          components={{ br: <br /> }}
        />
      )
    }

    const [openingHour] = todaysOpeningHours

    if (is24Hour(todaysOpeningHours)) {
      return t("screen.storeScreen.storeInformation.open24Hours", "Open 24/7")
    }

    const openTime = parseTime(openingHour?.openTime ?? "", today)
    const closeTime = parseTime(openingHour?.closeTime ?? "", today)

    if (!openTime || !closeTime) {
      return
    }

    const openTimeString = openTime.toLocaleString(DateTime.TIME_24_SIMPLE, { locale })
    const closeTimeString = closeTime.toLocaleString(DateTime.TIME_24_SIMPLE, { locale })

    return (
      <Trans
        ns="marketing-ls-store"
        i18nKey="screen.storeScreen.storeInformation.openingHours"
        defaults="Open from <br>{{openTime}} to {{closeTime}}"
        values={{ openTime: openTimeString, closeTime: closeTimeString }}
        components={{ br: <br /> }}
      />
    )
  }, [locale, store, t])

  const poisWithDistance = pois.map(poi => {
    const distance = getDistanceInMeters({ origin: store.coordinates, destination: poi.coordinates })
    return {
      ...poi,
      walkingTime: getWalkingTime(distance, i18n),
    }
  })

  const { format: formatRating } = intlNumberFormat({
    locale,
    options: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  })

  const aboutHasPoiReferences =
    about && (JSON.stringify(about).includes("FIRST_NEARBY_POI") || JSON.stringify(about).includes("SECOND_NEARBY_POI"))

  return (
    <section ref={sectionRef} className={classNames("mx-auto w-full max-w-screen-xl px-6", className)}>
      <div className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-2">
        <StoreInformationSection icon={CalendarIcon}>
          <p className="label1">{openingHoursText}</p>
        </StoreInformationSection>
        <StoreInformationSection icon={StarIcon}>
          <p className="label1">
            {t("screen.storeScreen.storeInformation.reviewsCount", {
              defaultValue: "{count, plural, =0 {No reviews} one {{rating} (# review)} other {{rating} (# reviews)}}",
              rating: formatRating(store.rating ?? 0),
              count: store.reviewCount,
            })}
          </p>
        </StoreInformationSection>
        {pois.length !== 0 && (
          <StoreInformationSection icon={WalkIcon}>
            <ul>
              {poisWithDistance.map(poi => (
                <StoreInformationPoi key={poi.slug} poi={poi} city={store.city} />
              ))}
            </ul>
          </StoreInformationSection>
        )}
      </div>
      {about && aboutHasPoiReferences && !!interpolations.FIRST_NEARBY_POI && !!interpolations.SECOND_NEARBY_POI && (
        <Truncate
          numberOfLines={4}
          onClick={(isOpen: boolean) =>
            Analytics.track(InteractionEvents.TruncateToggled, {
              name: "StoreInformation",
              open: isOpen,
            })
          }>
          <RichText
            field={about}
            interpolations={interpolations}
            className="mt-8 [&>*]:text-neutral-800 [&_a]:font-normal [&_a]:text-neutral-800 [&_a]:underline"
          />
        </Truncate>
      )}
    </section>
  )
}
